import { Component, OnInit, OnDestroy, AfterViewChecked, ElementRef, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';

import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { PasswordPolicyStatus } from '../../models/password-policy-status.model';

type PwxLevel = 'notice' | 'warning' | 'critical' | 'expired';

@Component({
  selector: 'password-policy-banner',
  template: `
    <div *ngIf="visible" class="pwx-bar"
         [class.pwx-warning]="level === 'warning'"
         [class.pwx-critical]="level === 'critical' || level === 'expired'"
         [attr.role]="level === 'critical' || level === 'expired' ? 'alert' : 'status'">
      <span class="pwx-icon" aria-hidden="true"><i class="fa" [ngClass]="iconClass"></i></span>
      <span class="pwx-text">
        <span class="pwx-headline">{{ headline }}</span>
        <span class="pwx-detail">{{ detail }}</span>
      </span>
      <a class="pwx-btn" [routerLink]="['/user-profile']" [queryParams]="{ changePassword: true }">
        <span class="pwx-btn-long">Change password</span>
        <span class="pwx-btn-short">Update</span>
      </a>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .pwx-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1035;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 10px 24px;
      font-size: 13px;
      line-height: 1.4;
      box-sizing: border-box;
      width: 100%;
      color: #fff;
      background: #2f80b8;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
    .pwx-bar.pwx-warning { background: #c98a1f; }
    .pwx-bar.pwx-critical { background: #c0392b; }
    .pwx-icon {
      flex-shrink: 0;
      width: 26px;
      height: 26px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .pwx-icon i { font-size: 14px; }
    .pwx-text {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-wrap: wrap;
      column-gap: 8px;
      row-gap: 2px;
    }
    .pwx-headline { font-weight: 600; }
    .pwx-detail { opacity: 0.9; }
    .pwx-btn {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 30px;
      padding: 0 16px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.18);
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      white-space: nowrap;
      cursor: pointer;
    }
    .pwx-btn:hover { background: rgba(255, 255, 255, 0.3); color: #fff; }
    .pwx-btn-short { display: none; }
    @media (max-width: 767px) {
      .pwx-bar { padding: 8px 16px; gap: 10px; font-size: 12px; }
      .pwx-detail { display: none; }
      .pwx-btn-long { display: none; }
      .pwx-btn-short { display: inline; }
    }
  `]
})
export class PasswordPolicyBannerComponent implements OnInit, AfterViewChecked, OnDestroy {

  status: PasswordPolicyStatus;
  level: PwxLevel;
  headline = '';
  detail = '';
  iconClass = '';
  visible = false;

  private loginStatusSubscription: any;
  private policyChangedSubscription: any;
  private pollIntervalHandle: any;
  private routerSubscription: any;
  private lastMeasuredHeight = 0;
  private onProfilePage = false;

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private router: Router,
    @Inject(DOCUMENT) private document: Document) {
  }

  ngOnInit() {
    this.onProfilePage = this.router.url.startsWith('/user-profile');
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.onProfilePage = event.urlAfterRedirects.startsWith('/user-profile');
        this.updateVisibility();
      }
    });

    this.loginStatusSubscription = this.authService.getLoginStatusEvent().subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.loadStatus();
      } else {
        this.status = null;
        this.updateVisibility();
      }
    });

    // Login/logout alone doesn't cover every case that can change this user's policy status
    // mid-session: completing the forced password-change flow, or an admin assigning a new
    // policy to their own account while already logged in, don't fire a login/logout
    // transition at all. This fires an immediate refresh for both of those; the poll below
    // is a robust fallback in case anything else ever changes status without going through
    // either path.
    this.policyChangedSubscription = this.authService.passwordPolicyChanged.subscribe(() => {
      if (this.authService.isLoggedIn) {
        this.loadStatus();
      }
    });

    this.pollIntervalHandle = setInterval(() => {
      if (this.authService.isLoggedIn) {
        this.loadStatus();
      }
    }, 60000);

    if (this.authService.isLoggedIn) {
      this.loadStatus();
    }
  }

  ngAfterViewChecked() {
    // Not dismissible by design - same as before this redesign, disappears only after a
    // successful password change. Height is measured (not hardcoded) since two-line wrapping
    // on narrow screens changes it; cheap no-op when unchanged, only runs while visible.
    if (!this.visible) {
      return;
    }
    const barEl = this.elementRef.nativeElement.querySelector('.pwx-bar');
    const height = barEl ? barEl.offsetHeight : 0;
    if (height && height !== this.lastMeasuredHeight) {
      this.lastMeasuredHeight = height;
      this.setBodyOffset(height);
    }
  }

  ngOnDestroy() {
    if (this.loginStatusSubscription) {
      this.loginStatusSubscription.unsubscribe();
    }
    if (this.policyChangedSubscription) {
      this.policyChangedSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.pollIntervalHandle) {
      clearInterval(this.pollIntervalHandle);
    }
    this.setBodyOffset(0);
  }

  private loadStatus() {
    this.accountService.getPasswordPolicyStatus().subscribe(
      (status: PasswordPolicyStatus) => {
        this.status = status;
        this.computeLevelAndCopy();
        this.updateVisibility();
      },
      () => {
        this.status = null;
        this.updateVisibility();
      });
  }

  private computeLevelAndCopy() {
    if (!this.status || !this.status.shouldShowBanner) {
      return;
    }

    const daysRemaining = this.status.daysRemaining;
    const dateText = this.status.requiredByDate
      ? new Date(this.status.requiredByDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      : '';

    if (this.status.isOverdue) {
      this.level = 'expired';
      this.iconClass = 'fa-lock';
      this.headline = 'Your password has expired';
      this.detail = 'Set a new password now to keep using Motivus.';
    } else if (daysRemaining <= 0) {
      this.level = 'critical';
      this.iconClass = 'fa-exclamation-circle';
      this.headline = 'Your password expires today';
      this.detail = (dateText ? ('on ' + dateText + '. ') : '') + 'Change it now to avoid being signed out.';
    } else if (daysRemaining <= 2) {
      this.level = 'warning';
      this.iconClass = 'fa-exclamation-triangle';
      this.headline = 'Your password expires in ' + daysRemaining + (daysRemaining === 1 ? ' day' : ' days');
      this.detail = (dateText ? ('on ' + dateText + '. ') : '') + 'Update it now to keep uninterrupted access.';
    } else {
      this.level = 'notice';
      this.iconClass = 'fa-clock-o';
      this.headline = 'Your password expires in ' + daysRemaining + ' days';
      this.detail = dateText ? ('on ' + dateText + '.') : '';
    }
  }

  private updateVisibility() {
    this.visible = !!(this.status && this.status.shouldShowBanner) && !this.onProfilePage;
    if (!this.visible) {
      this.lastMeasuredHeight = 0;
      this.setBodyOffset(0);
    }
  }

  private setBodyOffset(height: number) {
    if (height > 0) {
      this.document.body.classList.add('has-password-banner');
      this.document.documentElement.style.setProperty('--pwx-banner-height', height + 'px');
    } else {
      this.document.body.classList.remove('has-password-banner');
      this.document.documentElement.style.removeProperty('--pwx-banner-height');
    }
  }
}
